/**
 * What the Fix Location screen should tell the person after the server answers.
 *
 * The API answers HTTP 200 with `success: true` and sometimes a `warnings` list, and a refusal (a rate limit, a
 * branch the person may not act in, a daily limit) arrives as a normal response with `success: false`, not as a thrown
 * error. A screen that treats "the request finished" as "it worked" closes itself on a refusal and the person believes the
 * pin was saved. This reads the answer for what it says.
 */

export interface LocationApiAnswer {
  readonly success: boolean;
  readonly error?: { readonly code: string; readonly message: string };
  readonly warnings?: ReadonlyArray<{ readonly code: string; readonly message: string }>;
}

export type LocationOutcome =
  /** It worked; nothing to say. */
  | { readonly kind: 'ok' }
  /** It worked, but the person must know something (for example the pin could not be marked as theirs). */
  | { readonly kind: 'warning'; readonly message: string }
  /** It did NOT work; the message is the server's own words when it gave any. */
  | { readonly kind: 'failed'; readonly message: string };

/** Did the server save the pin but fail to mark it as placed by hand? The next Save is then a retry of that marking. */
export function pinNotLockedIn(answer: LocationApiAnswer): boolean {
  return answer.success && (answer.warnings ?? []).some((w) => w.code === 'PIN_NOT_LOCKED');
}

export function locationOutcomeOf(answer: LocationApiAnswer, fallbackFailure: string): LocationOutcome {
  if (!answer.success) return { kind: 'failed', message: answer.error?.message?.trim() || fallbackFailure };
  const warnings = (answer.warnings ?? []).map((w) => w.message.trim()).filter((m) => m.length > 0);
  return warnings.length > 0 ? { kind: 'warning', message: warnings.join(' ') } : { kind: 'ok' };
}
